import {
  Gift, Calendar, Store, Download, Clock, Building2,
  MapPin, Phone, Globe, Shield, ExternalLink,
} from "lucide-react";
import { API, axios, formatDateUS, openExternal } from "@/api";
import { toast } from "sonner";
import { trackLinkClick } from "@/lib/track";
import { DetailRow, humanize } from "./offerUtils";
import { useOfferDetail } from "./OfferDetailContext";
import ExternalLinkButtons from "@/components/shared/ExternalLinkButtons";

export default function OfferDetailsCard() {
  const { offer, businessId, cc, getText } = useOfferDetail();

  return (
    <>
      {/* Seasonal Availability */}
      {offer.out_of_season && (
        <div data-detail-card className="bg-gradient-to-r from-slate-100 to-slate-50 border border-slate-300" data-testid="seasonal-notice">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-slate-500" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-600">Currently Unavailable</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                This is a seasonal offer ({offer.seasonal_label || "limited time"}).
                {offer.next_available_month ? ` Returns in ${offer.next_available_month}.` : " Check back later."}
              </p>
            </div>
          </div>
        </div>
      )}
      {offer.seasonal_label && offer.seasonal_label !== "Year-round" && !offer.out_of_season && (
        <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="seasonal-active">
          <Calendar className="w-4 h-4 text-emerald-500" />
          <span className="text-xs font-semibold text-emerald-600">Seasonal offer — available now ({offer.seasonal_label?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || offer.seasonal_label})</span>
        </div>
      )}

      {/* iter265.1 — Franchise location disclaimer.
          Set when an offer is inherited from a brand-level turbo_birthday
          record AND the brand operates on a franchise model (Subway,
          McDonald's, Taco Bell, etc.). Individual locations may not honor
          the corporate birthday program, so we soft-warn the user. */}
      {offer.offer_confidence_note && (
        <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl" data-testid="franchise-disclaimer">
          <Shield className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <span className="text-xs font-medium text-amber-700 leading-snug">
            {offer.offer_confidence_note}
          </span>
        </div>
      )}

      {/* Signup Bonus */}
      {offer.signup_bonus && (
        <div data-detail-card className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200" data-testid="signup-bonus-section">
          <div className="flex items-center gap-2 mb-1">
            <Gift className="w-5 h-5 text-violet-500" />
            <span className="font-semibold text-violet-800">Signup Perk</span>
          </div>
          <p className="text-sm text-violet-700">{offer.signup_bonus}</p>
          <p className="text-[10px] text-violet-400 mt-1">This bonus is separate from the birthday deal</p>
        </div>
      )}

      {/* Deal Details */}
      <div data-detail-card="white" data-testid="deal-details-card">
        <h3 className="font-semibold text-slate-800 mb-2">Deal Details</h3>
        <div className="divide-y divide-slate-100">
          <DetailRow label="Reward Type" value={humanize(offer.offer_reward_type)} icon={Gift} />
          {offer.reward_format && <DetailRow label="Reward Format" value={humanize(offer.reward_format)} icon={Gift} />}
          <DetailRow label="Valid Window" value={offer.offer_window_days <= 1 ? "Birthday only" : `${offer.offer_window_days} days`} icon={Calendar} />
          {offer.seasonal_availability && offer.seasonal_availability !== "unknown" && offer.seasonal_availability !== "Unknown" && (
            <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
              <span className="text-sm text-slate-500 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />Availability</span>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                offer.seasonal_availability === "year_round" || offer.seasonal_availability === "Year-round"
                  ? "bg-emerald-50 text-emerald-600"
                  : "bg-amber-50 text-amber-600"
              }`} data-testid="seasonal-badge">
                {offer.seasonal_availability === "year_round" ? "Year-round" : offer.seasonal_availability.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
              </span>
            </div>
          )}
          <DetailRow label="Redemption" value={humanize(offer.offer_redemption_channel) || "In-Store"} icon={Store} />
          <DetailRow label="Requires App" value={offer.offer_requires_app ? "Yes" : "No"} valueColor={offer.offer_requires_app ? "text-amber-600" : "text-green-600"} icon={Download} />
          <DetailRow label="Purchase Required" value={offer.offer_purchase_required ? "Yes" : "No"} valueColor={offer.offer_purchase_required ? "text-amber-600" : "text-green-600"} />
          {offer.rewards_reset && <DetailRow label="Rewards Reset" value={offer.rewards_reset} icon={Clock} />}
          {offer.offer_expires_on && (
            <DetailRow
              label="Offer Expires"
              value={`${formatDateUS(offer.offer_expires_on)}${offer.deal_expires_in != null && offer.deal_expires_in <= 7 && offer.deal_expires_in >= 0 ? ` (${offer.deal_expires_in <= 1 ? "Today!" : `${offer.deal_expires_in} days left`})` : ""}`}
              valueColor={offer.deal_expires_in != null && offer.deal_expires_in <= 7 ? "text-red-600 font-bold" : "text-red-600"}
              icon={Calendar}
            />
          )}
          {offer.signup_by && <DetailRow label="Sign Up By" value={formatDateUS(offer.signup_by)} valueColor="text-amber-600" icon={Calendar} />}
        </div>
        {offer.deal_status === "active" && offer.deal_expires_in != null && offer.deal_expires_in <= 7 && offer.deal_expires_in >= 0 && (
          <button
            onClick={async () => {
              try {
                await axios.post(`${API}/tracker/${offer.business_id}/expiry-notify`, { opted_in: true });
                toast.success("You'll be notified before this deal expires");
              } catch { toast.error("Could not set reminder"); }
            }}
            className="mt-3 w-full py-2 px-4 rounded-xl bg-red-50 text-red-600 text-xs font-semibold border border-red-200 hover:bg-red-100 transition"
            data-testid="expiry-notify-btn"
          >
            Remind me before this expires
          </button>
        )}
        {offer.notes && <p className="text-sm text-slate-500 mt-3 pt-3 border-t border-slate-100 italic">{offer.notes}</p>}
      </div>

      {/* Get App Button */}
      {offer.offer_requires_app && (() => {
        const q = encodeURIComponent(offer.name);
        const ua = navigator.userAgent || "";
        const storeUrl = /iPhone|iPad|iPod/i.test(ua)
          ? `https://apps.apple.com/search?term=${q}`
          : `https://play.google.com/store/search?q=${q}&c=apps`;
        return (
          <div data-detail-card className="bg-gradient-to-r from-indigo-50 to-sky-50 border border-indigo-200" data-testid="get-app-section">
            <div className="flex items-center gap-2 mb-3">
              <Download className="w-5 h-5 text-indigo-600" />
              <span className="text-sm font-semibold text-indigo-800">App Required</span>
            </div>
            <p className="text-xs text-indigo-600 mb-3">This offer requires the {offer.name} app. Download it ahead of time.</p>
            <a
              href={storeUrl}
              target="_blank" rel="noopener noreferrer"
              onClick={(e) => { trackLinkClick("app_download", storeUrl, { business_id: businessId }); if (!openExternal(storeUrl, e)) toast.info("Link copied to clipboard"); }}
              className="w-full py-2.5 rounded-xl bg-indigo-600 text-white text-xs font-semibold text-center flex items-center justify-center gap-1.5 hover:bg-indigo-700 transition-colors"
              data-testid="app-download-btn"
            >
              <Download className="w-3.5 h-3.5" />Download {offer.name} App
            </a>
          </div>
        );
      })()}

      {/* Business Info */}
      {(() => {
        const nl = offer.nearest_location;
        const locs = offer.locations || [];
        const otherLocs = nl ? locs.filter(l => l.location_id !== nl.location_id) : locs;
        const tier = offer.location_tier || "national";
        const storeLocUrl = offer.store_locator_url;
        const bestAddress = nl?.address || (offer.address && !/multiple|see website|varies/i.test(offer.address) ? offer.address : null);
        const bestPhone = nl?.phone || (offer.phone && !/varies|n\/a|nan/i.test(offer.phone) ? offer.phone : null);
        const bestCity = nl ? `${nl.city}, ${nl.state} ${nl.zip_code || ""}`.trim() : (offer.city && !/multiple|varies/i.test(offer.city) ? `${offer.city}${offer.state && offer.state !== "All" ? `, ${offer.state}` : ""}` : null);
        const locName = nl?.location_name;
        const mapsSearchUrl = `https://www.google.com/maps/search/${encodeURIComponent(offer.name + " near me")}`;
        const tierLabel = tier === "national" ? "National Chain" : tier === "regional" ? `Regional · ${offer.coverage || offer.state || ""}` : "Local";
        const tierColor = tier === "national" ? "bg-sky-100 text-sky-700" : tier === "regional" ? "bg-teal-100 text-teal-700" : "bg-emerald-100 text-emerald-700";
        const locCount = locs.length > 1 ? `${locs.length} locations` : null;

        return (
          <div data-detail-card="white" data-testid="business-info-card">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-5 h-5 text-sky-500" />
              <h3 className="font-semibold text-slate-800">Business Info</h3>
              <span className={`text-[10px] ${tierColor} px-1.5 py-0.5 rounded-full font-semibold`}>{tierLabel}</span>
              {locCount && <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-medium">{locCount}</span>}
            </div>
            <div className="divide-y divide-slate-100">
              {locName && <DetailRow label="Nearest Store" value={locName} icon={Store} />}
              {bestAddress && <DetailRow label="Address" value={bestAddress} icon={Building2} />}
              {bestCity && <DetailRow label="City" value={bestCity} icon={MapPin} />}
              {bestPhone && !/nan/i.test(bestPhone) && (
                <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
                  <span className="text-sm text-slate-500 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />Phone</span>
                  <a href={`tel:${bestPhone}`} className="text-sm font-medium text-sky-600 hover:underline" data-testid="phone-link">{bestPhone}</a>
                </div>
              )}
              {offer.website && (
                <div className="flex items-center justify-between py-2.5">
                  <span className="text-sm text-slate-500 flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" />Website</span>
                  <a href={offer.website} target="_blank" rel="noopener noreferrer" onClick={(e) => { trackLinkClick("website", offer.website, { business_id: businessId }); if (!openExternal(offer.website, e)) toast.info("Link copied to clipboard"); }} className="text-sm font-medium text-sky-600 hover:underline flex items-center gap-1" data-testid="website-detail-link">
                    Visit site<ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </div>
            <div className="mt-3 flex flex-col gap-2">
              {storeLocUrl && (
                <a href={storeLocUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => { trackLinkClick("store_locator", storeLocUrl, { business_id: businessId }); if (!openExternal(storeLocUrl, e)) toast.info("Link copied to clipboard"); }} className="w-full flex items-center justify-center gap-2 py-2.5 bg-sky-50 border border-sky-200 rounded-xl text-sm font-semibold text-sky-700 hover:bg-sky-100 transition" data-testid="store-locator-btn">
                  <Store className="w-4 h-4" />Official Store Locator<ExternalLink className="w-3 h-3" />
                </a>
              )}
              <a href={mapsSearchUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => { trackLinkClick("google_maps", mapsSearchUrl, { business_id: businessId }); if (!openExternal(mapsSearchUrl, e)) toast.info("Link copied to clipboard"); }} className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition ${storeLocUrl ? "bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100" : "bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-100"}`} data-testid="find-nearest-btn">
                <MapPin className="w-4 h-4" />Find on Google Maps<ExternalLink className="w-3 h-3" />
              </a>
              {(() => {
                const termsSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(`"${offer.name}" birthday reward terms conditions official`)}`;
                const directUrl = offer.verification_url && !offer.verification_url.includes("google.com/search") && offer.verification_url !== offer.website
                  ? offer.verification_url : null;
                return (
                  <>
                    {directUrl && (
                      <a href={directUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => { trackLinkClick("verification_url", directUrl, { business_id: businessId }); if (!openExternal(directUrl, e)) toast.info("Link copied to clipboard"); }} className="w-full flex items-center justify-center gap-2 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-sm font-semibold text-emerald-700 hover:bg-emerald-100 transition" data-testid="verification-url-btn">
                        <Shield className="w-4 h-4" />View Official Terms<ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <a href={termsSearchUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => { trackLinkClick("terms_search", termsSearchUrl, { business_id: businessId }); if (!openExternal(termsSearchUrl, e)) toast.info("Link copied to clipboard"); }} className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition ${directUrl ? "bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100" : "bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100"}`} data-testid="search-terms-btn">
                      <Shield className="w-4 h-4" />Search Official Terms<ExternalLink className="w-3 h-3" />
                    </a>
                  </>
                );
              })()}
            </div>
            {/* External Link Buttons (Open App / Visit Website) */}
            {(offer.app_store_url || offer.website_url) && (
              <div className="mt-3" data-testid="offer-external-links">
                <ExternalLinkButtons app_store_url={offer.app_store_url} website_url={offer.website_url} business_name={offer.name} />
              </div>
            )}
            {otherLocs.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-500 mb-2">Other locations nearby</p>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {otherLocs.map((loc, i) => (
                    <a key={i} href={`https://www.google.com/maps/search/${encodeURIComponent(offer.name + " " + (loc.address || loc.city))}`} target="_blank" rel="noopener noreferrer" onClick={(e) => { if (!openExternal(`https://www.google.com/maps/search/${encodeURIComponent(offer.name + " " + (loc.address || loc.city))}`, e)) toast.info("Link copied"); }} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2 hover:bg-slate-100 transition" data-testid={`other-loc-${i}`}>
                      <span className="text-slate-700 font-medium truncate">{loc.location_name || `${loc.city}, ${loc.state}`}</span>
                      <span className="text-slate-400 flex items-center gap-1 flex-shrink-0 ml-2">{loc.city}<ExternalLink className="w-2.5 h-2.5" /></span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Dietary & Ethical */}
      {(offer.dietary?.halal_status !== "none" || offer.dietary?.vegan_status !== "none" || offer.dietary?.vegetarian_status !== "none" || offer.dietary?.gluten_free_certified || offer.dietary?.gluten_free_friendly) && (
        <div data-detail-card="white" data-testid="dietary-card">
          <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2"><Shield className="w-5 h-5 text-green-500" />Dietary Info</h3>
          <div className="divide-y divide-slate-100">
            {offer.dietary?.halal_status === "certified" && (
              <DetailRow label="Halal Status" value={`Certified${offer.dietary.halal_certifier ? ` — ${offer.dietary.halal_certifier}` : ""}`} valueColor="text-green-600" />
            )}
            {offer.dietary?.halal_status === "friendly" && (
              <DetailRow label="Halal Status" value="Halal Friendly" valueColor="text-green-500" />
            )}
            {offer.dietary?.vegan_status === "fully_vegan" && (
              <DetailRow label="Vegan Status" value="Fully Vegan" valueColor="text-green-600" />
            )}
            {offer.dietary?.vegan_status === "friendly" && (
              <DetailRow label="Vegan Status" value="Vegan Options Available" valueColor="text-lime-600" />
            )}
            {offer.dietary?.vegetarian_status === "fully_vegetarian" && (
              <DetailRow label="Vegetarian Status" value="Fully Vegetarian" valueColor="text-green-600" />
            )}
            {offer.dietary?.vegetarian_status === "friendly" && (
              <DetailRow label="Vegetarian Status" value="Vegetarian Options Available" valueColor="text-lime-600" />
            )}
            {offer.dietary?.gluten_free_certified && (
              <DetailRow label="Gluten Free" value="Certified Gluten Free" valueColor="text-amber-600" />
            )}
            {offer.dietary?.gluten_free_friendly && !offer.dietary?.gluten_free_certified && (
              <DetailRow label="Gluten Free" value="Gluten Free Options Available" valueColor="text-amber-500" />
            )}
            {offer.dietary?.alcohol_served != null && (
              <DetailRow label="Alcohol Served" value={offer.dietary.alcohol_served ? "Yes" : "No"} />
            )}
          </div>
          {offer.dietary?.dietary_confidence && (
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
              <span>Dietary confidence:</span>
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className={`w-2 h-2 rounded-full ${i <= offer.dietary.dietary_confidence ? "bg-green-400" : "bg-slate-200"}`} />
                ))}
              </div>
              <span className="text-slate-400">({offer.dietary.dietary_source === "legacy_data" ? "Legacy data" : humanize(offer.dietary.dietary_source || "")})</span>
            </div>
          )}
          <div className="mt-3 pt-3 border-t border-dashed border-slate-200 space-y-1.5" data-testid="dietary-confirmation-prompts">
            {(offer.dietary?.halal_status === "friendly" || offer.dietary?.halal_status === "certified") && (
              <p className="text-[10px] text-green-600 italic" data-testid="confirm-halal-prompt">{getText("confirm_halal")}</p>
            )}
            {(offer.dietary?.vegan_status === "friendly" || offer.dietary?.vegan_status === "fully_vegan") && (
              <p className="text-[10px] text-lime-600 italic" data-testid="confirm-vegan-prompt">{getText("confirm_vegan")}</p>
            )}
            {(offer.dietary?.gluten_free_certified || offer.dietary?.gluten_free_friendly) && (
              <p className="text-[10px] text-amber-600 italic" data-testid="confirm-gluten-free-prompt">{getText("confirm_gluten_free")}</p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
